import { ChatMessage } from '../types/chat';
import { isUntranscribedVoiceNote } from './voiceNoteHelper';

/**
 * Cola offline del chat IA (KAN-218) — helpers puros.
 * Spec: docs/superpowers/specs/2026-09-12-cola-offline-chat-design.md
 */

export type ChatMessageStatus = 'pendiente' | 'enviado' | 'error';

export interface ChatConversation {
  id: string;
  sessionId: string;
  title: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
  /** Conversación de soporte: id de la conversación con la IA a la que se vuelve al cerrar (KAN-181). */
  supportOriginId?: string;
}

export const MERGE_MAX_MENSAJES = 5;
export const MERGE_MAX_CHARS = 2000;
export const MAX_CONVERSACIONES = 4;
export const CONVERSATIONS_STORAGE_KEY = 'campo_manager_chat_conversations';
export const LEGACY_MESSAGES_KEY = 'campo_manager_chat_messages';

/**
 * Arma lotes de mensajes user pendientes (en orden) respetando tope de
 * cantidad y caracteres acumulados. Un mensaje individual más largo que el
 * máximo de caracteres viaja solo en su lote (no se parte).
 */
export function splitPendingIntoBatches(
  messages: ChatMessage[],
  maxMessages: number = MERGE_MAX_MENSAJES,
  maxChars: number = MERGE_MAX_CHARS
): ChatMessage[][] {
  const pendings = messages.filter((m) => m.role === 'user' && m.status === 'pendiente');
  const batches: ChatMessage[][] = [];
  let current: ChatMessage[] = [];
  let currentChars = 0;

  for (const m of pendings) {
    // Nota de voz sin transcribir (KAN-225): no puede viajar como texto y lo
    // que viene después no debe adelantarse — el lote termina acá.
    if (isUntranscribedVoiceNote(m)) break;
    const len = m.content.length;
    // Si el lote actual ya tiene contenido y agregarlo excede algún tope,
    // cerrar el lote primero.
    if (current.length > 0 && (current.length >= maxMessages || currentChars + len > maxChars)) {
      batches.push(current);
      current = [];
      currentChars = 0;
    }
    current.push(m);
    currentChars += len;
    if (current.length >= maxMessages || currentChars >= maxChars) {
      batches.push(current);
      current = [];
      currentChars = 0;
    }
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/** Concatena los contenidos del lote en orden, separados por línea vacía (un turno para el LLM). */
/** Metadatos del turno para el admin del bot: si viene de la cola offline, cuántas notas de voz y sus transcripciones. */
export interface OfflineTurnMeta {
  offlineQueued?: boolean;
  voiceNoteCount?: number;
  transcriptionTraceIds?: string[];
}

export function buildOfflineTurnMeta(batch: ChatMessage[]): OfflineTurnMeta {
  const voiceNotes = batch.filter((m) => !!m.voiceNote);
  const offline = voiceNotes.length > 0 || batch.some((m) => m.queuedOffline);
  if (!offline) return {};
  const traceIds = voiceNotes
    .map((m) => m.voiceNote?.transcriptionTraceId)
    .filter((id): id is string => !!id);
  return {
    offlineQueued: true,
    ...(voiceNotes.length ? { voiceNoteCount: voiceNotes.length } : {}),
    ...(traceIds.length ? { transcriptionTraceIds: traceIds } : {}),
  };
}

export function mergeBatchContents(batch: ChatMessage[]): string {
  return batch.map((m) => m.content).join('\n\n');
}

/** Título corto de conversación a partir del primer mensaje (~30 chars sin cortar palabras). */
export function buildConversationTitle(content: string): string {
  const clean = content.replace(/\s+/g, ' ').trim();
  if (clean.length <= 30) return clean;
  const cut = clean.slice(0, 30);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 15 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

export function createConversationId(): string {
  return `conv_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function createSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Agrega la conversación evictando la más vieja (updatedAt) si se supera el
 * máximo. Solo cuentan conversaciones con mensajes (las vacías no se persisten).
 */
export function addConversationWithEviction(
  list: ChatConversation[],
  conv: ChatConversation,
  max: number = MAX_CONVERSACIONES
): ChatConversation[] {
  const next = [...list, conv].filter((c) => c.messages.length > 0 || c.id === conv.id);
  if (next.length <= max) return next;
  const oldest = next.reduce((a, b) => (a.updatedAt <= b.updatedAt ? a : b));
  return next.filter((c) => c.id !== oldest.id);
}

/**
 * Migración one-shot: el historial único viejo (campo_manager_chat_messages)
 * pasa a ser una conversación. Devuelve null si no hay historial o es corrupto.
 */
export function migrateLegacyMessages(raw: string | null, now: number = Date.now()): ChatConversation | null {
  if (!raw) return null;
  try {
    const parsed: ChatMessage[] = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const first = parsed.find((m) => m.role === 'user' && m.content);
    return {
      id: createConversationId(),
      sessionId: createSessionId(),
      title: first ? buildConversationTitle(first.content) : 'Conversación anterior',
      messages: parsed,
      createdAt: now,
      updatedAt: now,
    };
  } catch {
    return null;
  }
}
